import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import HeightOutlinedIcon from '@mui/icons-material/HeightOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
import JoinRightOutlinedIcon from '@mui/icons-material/JoinRightOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {lighten} from '@mui/system/colorManipulator';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import DialogPanel from '@/components/common/DialogPanel';
import ValueLabel from '@/components/common/ValueLabelForPopUp';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import {UAV_POINT_CLOUD_STATUS, type UavPointCloud} from '@/domain/entities/UavPointCloud';
import {primaryColor} from '@/theme/theme';

//TYPES
type Props = {
  lidarTechnologies?: Array<LidarTechnology>;
  uavPointCloud: UavPointCloud;
  canDelete: boolean;
  canUpload: boolean;
  disabled?: boolean;
  onClose: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onReupload: () => void;
};

const UavPointCloudInfo: FC<Props> = ({
  uavPointCloud,
  lidarTechnologies,
  canDelete,
  canUpload,
  disabled = false,
  onDelete,
  onClose,
  onDownload,
  onReupload,
}) => {
  const {t} = useTranslation();

  const {flightDate, lidarTechnologyUuid, height, speed, overlapPercentage, comments, createdAt, status} =
    uavPointCloud;

  const lidarTechnologyLabel = lidarTechnologies?.find((tech) => tech.uuid === lidarTechnologyUuid)?.name || '-';

  const statusLabels: Record<UAV_POINT_CLOUD_STATUS, string> = {
    [UAV_POINT_CLOUD_STATUS.DATA_PENDING]: t('pointClouds.DATA_PENDING'),
    [UAV_POINT_CLOUD_STATUS.DATA_UPLOADED]: t('pointClouds.DATA_UPLOADED'),
  };

  return (
    <DialogPanel isOpen={true} label={`${t('pointClouds.detail')} UAV`} onClose={onClose}>
      <Stack sx={{p: 2}}>
        <ValueLabel
          value={createdAt.format('YYYY-MM-DD')}
          icon={<CalendarTodayOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.createdAt')}
        />
        <Divider />
        <ValueLabel
          value={statusLabels[status]}
          icon={<InfoOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.status')}
        />
        <Divider />
        <ValueLabel
          value={flightDate.format('YYYY-MM-DD')}
          icon={<FlightTakeoffOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.flightDate')}
        />
        <Divider />
        <ValueLabel
          value={lidarTechnologyLabel}
          icon={<SensorsOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.lidarTechnology')}
        />
        <Divider />
        <ValueLabel
          value={height}
          icon={<HeightOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.height')}
        />
        <Divider />
        <ValueLabel
          value={speed}
          icon={<SpeedOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.speed')}
        />
        <Divider />
        <ValueLabel
          value={overlapPercentage}
          icon={<JoinRightOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.overlapPercentage')}
        />
        <Divider />
        <ValueLabel
          direction="column"
          value={comments || '-'}
          icon={<InsertCommentOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.comments')}
        />
      </Stack>

      {status === UAV_POINT_CLOUD_STATUS.DATA_UPLOADED ? (
        <Stack direction="row" sx={{gap: 2, mt: 2, justifyContent: 'flex-end'}}>
          {canDelete && (
            <Button
              startIcon={<DeleteOutlineOutlinedIcon />}
              variant="outlined"
              sx={{color: 'primary.main', mt: 1}}
              onClick={onDelete}
            >
              {t('delete')}
            </Button>
          )}
          <Button
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
            sx={{bgcolor: lighten(primaryColor, 0.5), color: 'primary.main', mt: 1}}
            onClick={onDownload}
          >
            {t('download')}
          </Button>
        </Stack>
      ) : (
        <Stack sx={{mt: 2}}>
          <Typography sx={{color: 'grey.700'}}>{t('pointClouds.noDataUploaded')}</Typography>
          <Stack direction="row" sx={{gap: 2, justifyContent: 'flex-end'}}>
            {canDelete && (
              <Button
                startIcon={<DeleteOutlineOutlinedIcon />}
                variant="outlined"
                sx={{color: 'primary.main', mt: 1}}
                onClick={onDelete}
              >
                {t('delete')}
              </Button>
            )}
            {canUpload && (
              <Button
                startIcon={<FileUploadOutlinedIcon />}
                variant="contained"
                disabled={disabled}
                sx={{bgcolor: lighten(primaryColor, 0.5), color: 'primary.main', mt: 1}}
                onClick={onReupload}
              >
                {t('pointClouds.reupload')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </DialogPanel>
  );
};
export default UavPointCloudInfo;
